import type React from 'react'
import { type ReactNode, useContext, useEffect, useRef } from 'react'

import { useLocation, useParams } from '@tanstack/react-router'
import { useSessionStorage } from 'usehooks-ts'

import { useFirebaseUser } from '@/auth/useFirebaseUser'
import { useNotificationSound } from '@/hooks/useNotificationSound'
import { useMutedChannels } from '@/state/channels/useChannelPreferences'

import { DashboardAlertContext } from './DashboardAlertContext'
import { useOperationsSoundPreference } from './useOperationsSoundPreference'

export const NO_OPERATING_EVENING_ID = 0

export interface NotificationSoundProviderProps {
  regionId: string
  eveningId: number
  children: ReactNode
}

export const NotificationSoundProvider: React.FC<NotificationSoundProviderProps> = ({
  regionId,
  eveningId,
  children,
}) => {
  const { playNotificationSound } = useNotificationSound()
  const { alerts, synced } = useContext(DashboardAlertContext)
  const backlogConsumedFor = useRef<string | null>(null)

  const [playedSoundAlertIds, setPlayedSoundAlertIds] = useSessionStorage<string[]>(
    `soundPlayedAlerts_${regionId}_${eveningId}`,
    []
  )

  const [soundEnabled] = useOperationsSoundPreference()

  const params = useParams({ strict: false })
  const currentTeamId = 'teamId' in params ? params.teamId : undefined
  const openChannelId = useLocation({ select: (location) => location.pathname.split('/messages/channels/')[1] })
  const uid = useFirebaseUser()?.firebaseUser.uid
  const isChannelMuted = useMutedChannels()

  useEffect(() => {
    if (!synced) {
      return
    }
    const alertsKey = `${regionId}_${eveningId}`

    const newAlerts = alerts.filter((alert) => {
      if (playedSoundAlertIds.includes(alert._id)) return false

      return alert.type === 'teamMessage' || alert.type === 'transportRequest' || alert.type === 'channelMessage'
    })

    if (newAlerts.length === 0) {
      backlogConsumedFor.current = alertsKey
      return
    }

    const alertsToPlaySound = newAlerts.filter((alert) => {
      if (alert.type === 'teamMessage' && currentTeamId === alert.teamId) {
        return false
      }
      if (
        alert.type === 'channelMessage' &&
        (isChannelMuted(alert.channelId) || openChannelId === alert.channelId || alert.senderId === uid)
      ) {
        return false
      }
      return true
    })

    if (soundEnabled && backlogConsumedFor.current === alertsKey) {
      alertsToPlaySound.forEach((alert) => {
        const soundType = alert.type === 'transportRequest' ? 'transportRequest' : 'teamMessage'
        playNotificationSound(soundType, alert._id)
      })
    }

    backlogConsumedFor.current = alertsKey

    setPlayedSoundAlertIds((prev) => [...prev, ...newAlerts.map((a) => a._id)])
  }, [
    alerts,
    synced,
    regionId,
    eveningId,
    soundEnabled,
    playNotificationSound,
    currentTeamId,
    playedSoundAlertIds,
    setPlayedSoundAlertIds,
    isChannelMuted,
    openChannelId,
    uid,
  ])

  useEffect(() => {
    if (alerts.length === 0) {
      return
    }

    const currentAlertIds = new Set(alerts.map((a) => a._id))
    setPlayedSoundAlertIds((prev) => prev.filter((id) => currentAlertIds.has(id)))
  }, [alerts, setPlayedSoundAlertIds])

  return children
}
